import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
  NgZone,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as faceapi from 'face-api.js';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';

interface PhoneNumber {
  key: string;
  value: string;
}

@Component({
  standalone: false,
  selector: 'irembogov-irembo-selfie-verification',
  templateUrl: './irembo-selfie-verification.component.html',
  styleUrls: ['./irembo-selfie-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IremboSelfieVerificationComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  @Input() cameraNotAvailableErrorMessage!: string;
  @Input() verificationErrorMessage!: string;
  @Input() verificationCodeSentSuccessMessage!: string;
  @Input() phoneNumberSelectionTitle!: string;
  @Input() citizenPhotoNotAvailableMessage!: string;
  @Input() phoneNumberRequiredMessage!: string;
  @Input() parentComponent: any;
  @Input() customErrorMessages: { [key: string]: string } = {};

  @Input() maxAttempts = 3;
  @Input() preEnrollmentTempId: string | null = null;
  @Input() legalGuardianId: string | null = null;
  @Input() phoneConfig: any;

  @Input() useDetectFaceEndpoint = false;
  @Input() detectFaceEndpoint: string | null = null;

  private stream: MediaStream | null = null;
  private readonly facingMode = 'user';

  private detectionIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly DETECTION_INTERVAL_MS = 200;

  private detectionRunning = false;

  private static modelsLoaded = false;
  private static modelLoadPromise: Promise<void> | null = null;

  private stableFaceCount = 0;
  private readonly requiredStableFaces = 2;

  // UI state
  isCameraLoading = true;
  isReady = false;
  faceDetected = false;
  statusMessage = 'Position your face within the frame';
  cameraAvailable = true;
  isFrozen = false;
  isVerifying = false;
  verificationFailed = false;
  verificationSuccess = false;
  capturedImage: string | null = null;
  verificationErrorDetails!: string;

  verificationSteps = {
    left: { completed: false, detecting: false },
    right: { completed: false, detecting: false },
    center: { completed: false, detecting: false },
  };
  currentStep: 'left' | 'right' | 'center' = 'left';
  allStepsCompleted = false;

  private stepConfirmationCount = 0;
  private readonly requiredStepConfirmations = 4;

  // Anti-spoofing
  private consecutiveFaceLossCount = 0;
  private readonly maxAllowedFaceLoss = 10;

  private stepCompletionLocked = false;

  // Phone verification
  showPhoneVerification = false;
  citizenPhotoNotAvailableError = false;
  phoneNumberRequiredError = false;
  displayedPhoneVerificationMessage = '';
  @Input() phoneNumbers: PhoneNumber[] = [];
  showManualInput = false;
  phoneFormControl = new FormControl('', [
    Validators.required,
    Validators.pattern('\\+2507\\d{8}'),
  ]);
  attemptsFromApi = 0;

  phoneSelectControl = new FormControl();
  formattedPhoneNumbers: Array<{ label: string; value: string; key: string }> =
    [];
  selectedPhoneKey: string | null = null;

  showSuccessScreen = false;
  verificationRequestInProgress = false;
  codeWasSent = false;

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private translateService: TranslateService,
    private readonly cd: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  // ─────────────────────────────────────────────────────────────────────────────
  // Model Loading
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Called by the parent component (CustomSelfieVerificationComponent) in its
   * ngOnInit — long before the user clicks "Open camera". By the time the modal
   * opens and the camera fires `onplaying`, the models are already loaded and
   * detection starts immediately with zero wait.
   */
  static preload(): void {
    if (
      IremboSelfieVerificationComponent.modelsLoaded ||
      IremboSelfieVerificationComponent.modelLoadPromise
    ) {
      return;
    }

    IremboSelfieVerificationComponent.modelLoadPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri('/assets/models'),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri('/assets/models'),
    ])
      .then(() => {
        IremboSelfieVerificationComponent.modelsLoaded = true;
      })
      .catch(() => {
        // Reset so the next attempt can retry
        IremboSelfieVerificationComponent.modelLoadPromise = null;
      });
  }

  private preloadFaceDetectionModels(): void {
    IremboSelfieVerificationComponent.preload();
  }

  private async loadFaceDetectionModels(): Promise<void> {
    if (IremboSelfieVerificationComponent.modelsLoaded) {
      this.isReady = true;
      this.startDetectionLoop();
      return;
    }

    if (!IremboSelfieVerificationComponent.modelLoadPromise) {
      IremboSelfieVerificationComponent.preload();
    }

    try {
      await IremboSelfieVerificationComponent.modelLoadPromise;
      this.isReady = true;
      if (this.stream && !this.isFrozen && !this.isVerifying) {
        this.startDetectionLoop();
      }
    } catch (err: any) {
      this.verificationErrorDetails =
        'Could not load face detection. Please check your connection.';
    }

    this.cd.detectChanges();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Camera
  // ─────────────────────────────────────────────────────────────────────────────

  private async initializeCamera(): Promise<void> {
    this.stopDetectionLoop();
    this.stopCameraStream();

    this.faceDetected = false;
    this.stableFaceCount = 0;
    this.isFrozen = false;
    this.detectionRunning = false;
    this.isCameraLoading = true; // Show spinner immediately
    this.resetVerificationSteps();
    this.cd.detectChanges();

    try {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: this.facingMode },
            width: { max: 640 },
            height: { max: 480 },
          },
          audio: false,
        });
      } catch {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: this.facingMode } },
          audio: false,
        });
      }

      const video = this.videoElement?.nativeElement;
      if (!video) return;

      video.srcObject = this.stream;

      video.onloadedmetadata = () => {
        video.play().catch(() => {
          /* autoplay prevented, handled by onplaying */
        });
      };

      video.onplaying = () => {
        this.isCameraLoading = false;
        this.cameraAvailable = true;

        if (IremboSelfieVerificationComponent.modelsLoaded) {
          this.isReady = true;
          this.startDetectionLoop();
        } else {
          this.loadFaceDetectionModels();
        }

        this.cd.detectChanges();
      };

      setTimeout(() => {
        if (this.isCameraLoading && this.stream) {
          this.isCameraLoading = false;
          if (!this.isReady && IremboSelfieVerificationComponent.modelsLoaded) {
            this.isReady = true;
            this.startDetectionLoop();
          }
          this.cd.detectChanges();
        }
      }, 3000);
    } catch {
      this.handleCameraError();
    }
  }

  private handleCameraError(): void {
    this.stopCameraStream();
    this.isCameraLoading = false;
    this.cameraAvailable = false;
    this.cd.detectChanges();
  }

  private stopCameraStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
    }
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }
  }

  private startDetectionLoop(): void {
    this.stopDetectionLoop();
    this.stableFaceCount = 0;
    this.faceDetected = false;
    this.detectionRunning = false;

    this.detectionIntervalId = setInterval(() => {
      this.detectFace();
    }, this.DETECTION_INTERVAL_MS);
  }

  private stopDetectionLoop(): void {
    if (this.detectionIntervalId !== null) {
      clearInterval(this.detectionIntervalId);
      this.detectionIntervalId = null;
    }
  }

  private detectFace(): void {
    if (
      !this.videoElement?.nativeElement ||
      this.isFrozen ||
      this.isVerifying ||
      !this.isReady ||
      this.detectionRunning ||
      !this.stream ||
      this.stepCompletionLocked ||
      this.isCameraLoading
    ) {
      return;
    }

    const video = this.videoElement.nativeElement;
    if (!video.videoWidth || !video.videoHeight) return;

    this.detectionRunning = true;

    this.zone.runOutsideAngular(async () => {
      try {
        const options = new faceapi.TinyFaceDetectorOptions({
          inputSize: 224,
          scoreThreshold: 0.5,
        });

        const detection = await faceapi
          .detectSingleFace(video, options)
          .withFaceLandmarks(true);

        this.zone.run(() => {
          if (
            !this.isFrozen &&
            !this.isVerifying &&
            !this.stepCompletionLocked
          ) {
            if (detection) {
              this.processFaceDetection(detection);
            } else {
              this.handleFaceLoss();
            }
          }
          this.detectionRunning = false;
        });
      } catch {
        this.zone.run(() => {
          this.detectionRunning = false;
        });
      }
    });
  }

  private processFaceDetection(
    detection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }>
  ): void {
    this.consecutiveFaceLossCount = 0;

    const orientation = this.determineFaceOrientation(detection);
    let changed = false;

    if (orientation === this.currentStep) {
      this.stepConfirmationCount++;

      if (!this.verificationSteps[this.currentStep].detecting) {
        this.verificationSteps[this.currentStep].detecting = true;
        changed = true;
      }

      if (this.stepConfirmationCount >= this.requiredStepConfirmations) {
        this.completeCurrentStep();
        return;
      }
    } else {
      if (this.stepConfirmationCount > 0) {
        this.stepConfirmationCount = 0;
      }
      if (this.verificationSteps[this.currentStep].detecting) {
        this.verificationSteps[this.currentStep].detecting = false;
        changed = true;
      }
    }

    const nowFaceDetected = orientation !== null;
    if (nowFaceDetected !== this.faceDetected) {
      this.faceDetected = nowFaceDetected;
      changed = true;
    }

    if (changed) {
      this.cd.detectChanges();
    }
  }

  private handleFaceLoss(): void {
    if (this.allStepsCompleted || this.stepCompletionLocked) return;

    this.stableFaceCount = 0;

    let changed = false;

    if (this.faceDetected) {
      this.faceDetected = false;
      changed = true;
    }

    if (
      this.currentStep === 'center' &&
      (this.verificationSteps.left.completed ||
        this.verificationSteps.right.completed)
    ) {
      this.consecutiveFaceLossCount++;
      if (this.consecutiveFaceLossCount >= this.maxAllowedFaceLoss) {
        this.resetAllSteps();
        return;
      }
    } else {
      this.consecutiveFaceLossCount = 0;
    }

    if (this.stepConfirmationCount > 0) {
      this.stepConfirmationCount = 0;
    }
    if (this.verificationSteps[this.currentStep].detecting) {
      this.verificationSteps[this.currentStep].detecting = false;
      changed = true;
    }

    if (changed) this.cd.detectChanges();
  }

  private resetAllSteps(): void {
    this.verificationSteps = {
      left: { completed: false, detecting: false },
      right: { completed: false, detecting: false },
      center: { completed: false, detecting: false },
    };
    this.currentStep = 'left';
    this.allStepsCompleted = false;
    this.stepConfirmationCount = 0;
    this.consecutiveFaceLossCount = 0;
    this.faceDetected = false;
    this.stepCompletionLocked = false;
    this.cd.detectChanges();
  }

  private determineFaceOrientation(
    detection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }>
  ): 'left' | 'right' | 'center' | null {
    try {
      const landmarks = detection.landmarks;
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const nose = landmarks.getNose();

      const faceBox = detection.detection.box;
      const videoWidth = this.videoElement?.nativeElement?.videoWidth || 640;
      const videoHeight = this.videoElement?.nativeElement?.videoHeight || 480;

      const leftEyeCenter = {
        x: leftEye.reduce((s, p) => s + p.x, 0) / leftEye.length,
        y: leftEye.reduce((s, p) => s + p.y, 0) / leftEye.length,
      };
      const rightEyeCenter = {
        x: rightEye.reduce((s, p) => s + p.x, 0) / rightEye.length,
        y: rightEye.reduce((s, p) => s + p.y, 0) / rightEye.length,
      };

      const noseTip = nose[6];
      const faceCenter = faceBox.x + faceBox.width / 2;
      const noseOffset = noseTip.x - faceCenter;
      const normalizedOffset = noseOffset / faceBox.width;

      const eyeDistance = Math.abs(rightEyeCenter.x - leftEyeCenter.x);
      const normalizedEyeDistance = eyeDistance / faceBox.width;

      const isCenteredInFrame =
        Math.abs(faceCenter - videoWidth / 2) < videoWidth * 0.25 &&
        Math.abs(faceBox.y + faceBox.height / 2 - videoHeight / 2) <
          videoHeight * 0.25;

      if (!isCenteredInFrame) return null;

      const eyeAngle = Math.abs(
        Math.atan2(
          rightEyeCenter.y - leftEyeCenter.y,
          rightEyeCenter.x - leftEyeCenter.x
        ) *
          (180 / Math.PI)
      );

      if (
        Math.abs(normalizedOffset) < 0.28 &&
        normalizedEyeDistance > 0.3 &&
        eyeAngle < 35
      ) {
        return 'center';
      }
      if (normalizedOffset > 0.15 && normalizedEyeDistance < 0.45) {
        return 'left';
      }
      if (normalizedOffset < -0.15 && normalizedEyeDistance < 0.45) {
        return 'right';
      }

      return null;
    } catch {
      return null;
    }
  }

  private completeCurrentStep(): void {
    this.verificationSteps[this.currentStep].completed = true;
    this.verificationSteps[this.currentStep].detecting = false;
    this.stepConfirmationCount = 0;

    if (this.currentStep === 'left') {
      this.currentStep = 'right';
      this.cd.detectChanges();
    } else if (this.currentStep === 'right') {
      this.currentStep = 'center';
      this.cd.detectChanges();
    } else {
      this.verificationSteps.center.detecting = true;
      this.allStepsCompleted = true;
      this.stepCompletionLocked = true;
      this.stopDetectionLoop();
      this.cd.detectChanges();

      setTimeout(() => {
        this.takePicture();
      }, 400);
    }
  }

  private resetVerificationSteps(): void {
    this.verificationSteps = {
      left: { completed: false, detecting: false },
      right: { completed: false, detecting: false },
      center: { completed: false, detecting: false },
    };
    this.currentStep = 'left';
    this.allStepsCompleted = false;
    this.stepConfirmationCount = 0;
    this.consecutiveFaceLossCount = 0;
    this.stepCompletionLocked = false;
  }

  getStepInstructions(): string {
    if (this.allStepsCompleted && this.isFrozen)
      return 'Perfect! Capturing image and validating face...';
    if (this.allStepsCompleted) return 'Perfect! Hold still...';

    switch (this.currentStep) {
      case 'left':
        return 'Please turn your face to the left';
      case 'right':
        return 'Please turn your face to the right';
      case 'center':
        return 'Please look straight at the camera';
      default:
        return 'Position your face within the frame';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Capture & Verification
  // ─────────────────────────────────────────────────────────────────────────────

  async takePicture(): Promise<void> {
    if (!this.videoElement?.nativeElement || !this.allStepsCompleted) return;

    try {
      this.isFrozen = true;
      const video = this.videoElement.nativeElement;

      const fullCanvas = document.createElement('canvas');
      fullCanvas.width = video.videoWidth;
      fullCanvas.height = video.videoHeight;
      const ctx = fullCanvas.getContext('2d');
      if (!ctx) {
        this.isFrozen = false;
        return;
      }

      // Mirror the image to match what the user saw on screen
      ctx.translate(fullCanvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      this.capturedImage = fullCanvas.toDataURL('image/jpeg', 0.7);

      if (this.useDetectFaceEndpoint && this.detectFaceEndpoint) {
        await this.processWithExternalDetection(fullCanvas);
      } else {
        await this.processWithFullImage(fullCanvas);
      }
    } catch (error) {
      console.error('Error taking picture:', error);
      this.isFrozen = false;
      await this.initializeCamera();
    }
  }

  private async processWithExternalDetection(
    fullCanvas: HTMLCanvasElement
  ): Promise<void> {
    try {
      this.isVerifying = true;
      this.stopCameraStream();
      this.cd.detectChanges();

      const fullImageBase64 = fullCanvas.toDataURL('image/jpeg', 0.8);
      const detectionResult = await this.detectFaceWithEndpoint(
        fullImageBase64
      );

      if (detectionResult) {
        const faceImage = await this.extractFaceFromBoundingBox(
          fullCanvas,
          detectionResult
        );
        if (faceImage) {
          this.capturedImage = faceImage;
          setTimeout(() => this.startVerification(faceImage), 500);
        } else {
          throw new Error('Failed to extract face from bounding box');
        }
      } else {
        throw new Error('Face detection failed');
      }
    } catch {
      this.handleFaceDetectionError();
    }
  }

  private async processWithFullImage(
    fullCanvas: HTMLCanvasElement
  ): Promise<void> {
    const maxDimension = 800;
    const aspectRatio = fullCanvas.width / fullCanvas.height;
    let targetWidth, targetHeight;

    if (fullCanvas.width > fullCanvas.height) {
      targetWidth = Math.min(maxDimension, fullCanvas.width);
      targetHeight = Math.round(targetWidth / aspectRatio);
    } else {
      targetHeight = Math.min(maxDimension, fullCanvas.height);
      targetWidth = Math.round(targetHeight * aspectRatio);
    }

    const resizedImage = await this.resizeImage(
      fullCanvas,
      targetWidth,
      targetHeight,
      0.85
    );
    this.capturedImage = resizedImage;
    this.stopCameraStream();
    this.cd.detectChanges();

    setTimeout(() => this.startVerification(resizedImage), 500);
  }

  private detectFaceWithEndpoint(base64Image: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.parentComponent || !this.detectFaceEndpoint) {
        return reject(
          new Error('Parent component or detection endpoint not available')
        );
      }

      this.parentComponent
        .submitFaceDetection({ selfie: base64Image })
        .pipe(
          finalize(() => {
            /* no side effects */
          })
        )
        .subscribe(
          (response: any) => {
            if (response?.status === true && response?.data?.response) {
              try {
                const parsed = JSON.parse(response.data.response);
                if (
                  parsed?.boundingBox?.status === true &&
                  parsed?.boundingBox?.boundingBox
                ) {
                  resolve(parsed.boundingBox);
                } else {
                  reject(
                    new Error(
                      parsed?.boundingBox?.message || 'Face detection failed'
                    )
                  );
                }
              } catch {
                reject(new Error('Invalid response format'));
              }
            } else {
              reject(
                new Error(response?.responseMessage || 'Face detection failed')
              );
            }
          },
          (error: HttpErrorResponse) => {
            reject(
              new Error(
                error?.error?.responseMessage ||
                  'Face detection endpoint failed'
              )
            );
          }
        );
    });
  }

  private async extractFaceFromBoundingBox(
    canvas: HTMLCanvasElement,
    boundingBoxData: any
  ): Promise<string | null> {
    try {
      const bb = boundingBoxData.boundingBox?.[0];
      if (!bb || bb.length !== 4) return null;

      const [x1, y1, x2, y2] = bb;
      const x = Math.round(x1),
        y = Math.round(y1);
      const w = Math.round(x2 - x1),
        h = Math.round(y2 - y1);

      if (
        x < 0 ||
        y < 0 ||
        w <= 0 ||
        h <= 0 ||
        x + w > canvas.width ||
        y + h > canvas.height
      ) {
        return null;
      }

      const faceCanvas = document.createElement('canvas');
      faceCanvas.width = w;
      faceCanvas.height = h;
      faceCanvas.getContext('2d')?.drawImage(canvas, x, y, w, h, 0, 0, w, h);

      const targetSize = Math.min(400, Math.max(w, h));
      const ar = w / h;
      const tw = ar > 1 ? targetSize : Math.round(targetSize * ar);
      const th = ar > 1 ? Math.round(targetSize / ar) : targetSize;

      return this.resizeImage(faceCanvas, tw, th, 0.85);
    } catch {
      return null;
    }
  }

  private handleFaceDetectionError(): void {
    this.isFrozen = false;
    this.isVerifying = false;
    this.verificationFailed = true;
    this.verificationErrorDetails =
      'Face detection failed. Please ensure your face is clearly visible and try again.';
    this.cd.detectChanges();
  }

  private resizeImage(
    src: HTMLCanvasElement,
    w: number,
    h: number,
    quality = 0.7
  ): Promise<string> {
    return new Promise(resolve => {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, w, h);
        resolve(c.toDataURL('image/jpeg', quality));
      } else {
        resolve(src.toDataURL('image/jpeg', quality));
      }
    });
  }

  async startVerification(base64Image: string): Promise<void> {
    const idToUse = this.preEnrollmentTempId ?? this.legalGuardianId;
    if (!idToUse || !this.parentComponent) {
      this.isVerifying = false;
      this.verificationFailed = true;
      this.verificationErrorDetails = 'Missing required data for verification.';
      this.cd.detectChanges();
      return;
    }

    try {
      this.isVerifying = true;
      this.cd.detectChanges();

      this.parentComponent
        .submitVerification({ selfie: base64Image })
        .pipe(
          finalize(() => {
            this.isVerifying = false;
            this.cd.detectChanges();
          })
        )
        .subscribe(
          (response: any) => {
            if (!response || response.status !== true || !response.data) {
              this.handleErrorResponse(response);
              return;
            }
            try {
              if (!response.data.response) {
                this.handleVerificationSuccess();
                return;
              }
              const obj = this.parseResponseData(response.data.response);
              if (!obj) {
                this.handleVerificationError({
                  message: 'Invalid response format',
                });
                return;
              }
              if (this.isSkipRequestSuccess(obj)) {
                this.handleSkipRequestSuccess();
                return;
              }
              this.handleVerificationSuccess();
            } catch {
              this.handleVerificationError({
                message: 'Error processing response',
              });
            }
          },
          (error: HttpErrorResponse) => this.handleErrorFromAPI(error)
        );
    } catch {
      this.handleVerificationError();
    }
  }

  private parseResponseData(d: string) {
    return JSON.parse(d);
  }

  private isSkipRequestSuccess(obj: any): boolean {
    return obj?.message?.includes(
      'Face verification skip request sent successfully'
    );
  }

  private handleSkipRequestSuccess(): void {
    this.codeWasSent = true;
    this.showSuccessScreen = true;
    this.cd.detectChanges();
  }

  private handleVerificationSuccess(): void {
    this.verificationSuccess = true;
    this.cd.detectChanges();
  }

  private handleErrorResponse(response: any): void {
    this.handleVerificationError({
      message: response?.responseMessage || 'Verification unsuccessful',
    });
  }

  private handleErrorFromAPI(error: HttpErrorResponse): void {
    this.isVerifying = false;
    this.verificationFailed = true;
    try {
      this.extractAndProcessErrorDetails(error);
    } catch {
      this.verificationErrorDetails =
        'Verification failed. Please ensure your face is clearly visible.';
    }
    this.checkForSpecialErrorCases();
  }

  private extractAndProcessErrorDetails(error: HttpErrorResponse): void {
    if (!error.error) {
      this.verificationErrorDetails = error.message || 'Verification failed.';
      return;
    }
    if (!error.error.responseMessage) {
      this.verificationErrorDetails =
        error.error.message || error.message || 'Verification failed.';
      return;
    }
    this.processResponseMessage(error.error.responseMessage);
  }

  private processResponseMessage(msg: string): void {
    let obj: any;
    try {
      obj = JSON.parse(msg);
    } catch {
      this.verificationErrorDetails = msg;
      this.checkForSpecialErrorCases();
      return;
    }
    if (!obj.message) {
      this.setErrorDetailsFromResponseObj(obj);
      this.checkForSpecialErrorCases();
      return;
    }
    this.processErrorMessage(obj.message);
  }

  private processErrorMessage(msg: string): void {
    const match = /attempts\s*:\s*(\d+)/i.exec(msg);
    if (match?.[1]) {
      const n = parseInt(match[1], 10);
      if (!Number.isNaN(n)) {
        this.attemptsFromApi = n;
        if (n >= 2) {
          this.showPhoneVerification = true;
          setTimeout(() => this.cd.detectChanges(), 0);
        }
      }
    }
    try {
      this.processErrorDetails(JSON.parse(msg));
    } catch {
      this.verificationErrorDetails = msg;
    }
    this.checkForSpecialErrorCases();
  }

  private processErrorDetails(obj: any): void {
    if (obj.errorDescription)
      this.verificationErrorDetails = obj.errorDescription;
    this.processAttemptsCount(obj.attempts);
  }

  private processAttemptsCount(n: number | undefined): void {
    if (n !== undefined) {
      this.attemptsFromApi = n;
      if (n >= 2) {
        this.showPhoneVerification = true;
        setTimeout(() => this.cd.detectChanges(), 0);
      }
    }
  }

  private setErrorDetailsFromResponseObj(obj: any): void {
    this.verificationErrorDetails = obj.statusCode
      ? `Error ${obj.statusCode}: ${obj.path || 'Unknown path'}`
      : 'Verification failed. Please try again.';
  }

  private handleVerificationError(error?: any): void {
    this.isVerifying = false;
    this.verificationFailed = true;
    if (error?.message) this.verificationErrorDetails = error.message;
    else if (error?.error?.responseMessage)
      this.verificationErrorDetails = error.error.responseMessage;
    else
      this.verificationErrorDetails =
        'Verification failed. Please ensure your face is clearly visible.';
    this.checkForSpecialErrorCases();
    this.cd.detectChanges();
  }

  private checkForSpecialErrorCases(): void {
    if (!this.verificationErrorDetails) return;
    const msg = this.verificationErrorDetails.toLowerCase();

    if (
      msg.includes('citizen photo is not available') ||
      msg.includes('citizen photo not available')
    ) {
      this.citizenPhotoNotAvailableError = true;
      this.phoneNumberRequiredError = false;
      this.showPhoneVerification = true;
      this.displayedPhoneVerificationMessage = this
        .citizenPhotoNotAvailableMessage
        ? this.translateService.instant(this.citizenPhotoNotAvailableMessage)
        : this.verificationErrorDetails;
      setTimeout(() => this.cd.detectChanges(), 0);
    } else if (
      msg.includes('phone number is required for skip selfie verification')
    ) {
      this.phoneNumberRequiredError = true;
      this.citizenPhotoNotAvailableError = false;
      this.showPhoneVerification = true;
      this.displayedPhoneVerificationMessage = this.phoneNumberRequiredMessage
        ? this.translateService.instant(this.phoneNumberRequiredMessage)
        : this.verificationErrorDetails;
      setTimeout(() => this.cd.detectChanges(), 0);
    }
  }

  private getCustomErrorMessage(msg: string): string | null {
    if (
      !this.customErrorMessages ||
      !Object.keys(this.customErrorMessages).length
    )
      return null;
    for (const [key, val] of Object.entries(this.customErrorMessages)) {
      if (msg.toLowerCase().includes(key.toLowerCase()))
        return this.translateService.instant(val);
    }
    return null;
  }

  getDisplayErrorMessage(): string {
    if (!this.verificationErrorDetails)
      return 'Something went wrong. Please try again or contact our team if the issue persists.';
    const custom = this.getCustomErrorMessage(this.verificationErrorDetails);
    if (custom) return custom;
    if (this.verificationErrorDetails.toLowerCase().includes('match')) {
      return 'Face verification unsuccessful. Please make sure your face is clearly visible and properly positioned.';
    }
    return 'Something went wrong. Please try again or contact our team if the issue persists.';
  }

  private formatPhoneNumbersForDropdown(): void {
    this.formattedPhoneNumbers = (this.phoneNumbers ?? []).map(p => ({
      label: p.value,
      value: p.value,
      key: p.key,
    }));
    if (this.formattedPhoneNumbers.length > 0) {
      this.formattedPhoneNumbers.push({
        label: this.translateService.instant('Enter manually'),
        value: 'manual',
        key: 'manual',
      });
    }
  }

  onPhoneNumberSelected(value: string): void {
    if (value === 'manual') {
      this.showManualInput = true;
      this.phoneFormControl.enable();
      this.phoneFormControl.reset();
      this.selectedPhoneKey = null;
    } else {
      this.showManualInput = false;
      this.phoneFormControl.setValue(value);
      this.phoneFormControl.disable();
      this.selectedPhoneKey =
        this.formattedPhoneNumbers.find(p => p.value === value)?.key ?? null;
    }
  }

  requestVerificationCode(): void {
    if (this.phoneFormControl.valid || this.phoneFormControl.disabled) {
      this.verificationRequestInProgress = true;
      this.parentComponent
        .submitVerification({
          selfie: this.capturedImage,
          phoneNumberKey: this.selectedPhoneKey || undefined,
          phoneNumber: this.selectedPhoneKey
            ? undefined
            : this.phoneFormControl.value,
        })
        .pipe(
          finalize(() => {
            this.verificationRequestInProgress = false;
            this.cd.detectChanges();
          })
        )
        .subscribe(
          (response: any) => {
            if (response?.status === true) {
              try {
                const obj = JSON.parse(response.data?.response ?? '{}');
                if (
                  obj?.message?.includes(
                    'Face verification skip request sent successfully'
                  )
                )
                  this.codeWasSent = true;
              } catch {
                /* ignore */
              }
              this.showSuccessScreen = true;
              this.cd.detectChanges();
            } else {
              this.verificationErrorDetails =
                response?.responseMessage || 'Verification code request failed';
              this.toastService.show({
                body: this.translateService.instant(
                  'Request verification error!'
                ),
                type: 'error',
                delay: 5000,
              });
              this.cd.detectChanges();
            }
          },
          (error: HttpErrorResponse) => {
            this.verificationErrorDetails =
              error?.error?.responseMessage ||
              'Verification code request failed.';
            this.verificationFailed = true;
            this.toastService.show({
              body: this.translateService.instant(
                'Request verification error!'
              ),
              type: 'error',
              delay: 5000,
            });
            this.cd.detectChanges();
          }
        );
    }
  }

  ngOnInit(): void {
    this.phoneFormControl.setValidators([
      Validators.required,
      Validators.pattern(this.phoneConfig?.pattern || '\\+2507\\d{8}'),
    ]);
    this.formatPhoneNumbersForDropdown();
    this.preloadFaceDetectionModels();
    this.initializeCamera();
  }

  async retryVerification(): Promise<void> {
    this.isFrozen = false;
    this.isVerifying = false;
    this.verificationFailed = false;
    this.capturedImage = null;
    this.faceDetected = false;
    this.verificationErrorDetails = '';
    this.stableFaceCount = 0;
    this.citizenPhotoNotAvailableError = false;
    this.phoneNumberRequiredError = false;
    this.displayedPhoneVerificationMessage = '';
    this.showPhoneVerification = false;
    this.resetVerificationSteps();
    this.stopDetectionLoop();
    this.detectionRunning = false;
    this.cd.detectChanges();
    await this.initializeCamera();
  }

  continueWithSuccess(): void {
    this.activeModal.close({
      status: 'verification_successful',
      selfie: this.capturedImage,
    });
  }

  closeSuccessScreen(): void {
    this.activeModal.close({
      status: 'verification_code_requested',
      phoneNumber: this.phoneFormControl.value,
      phoneKey: this.selectedPhoneKey,
      codeWasSent: this.codeWasSent,
    });
  }

  cancel(): void {
    this.stopDetectionLoop();
    this.stopCameraStream();
    this.activeModal.dismiss();
  }

  ngOnDestroy(): void {
    this.stopDetectionLoop();
    this.stopCameraStream();
  }
}
